import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Heart, Users, Sparkles, Quote, Calendar, TrendingUp, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

const FounderPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="text-[#1C3A7A] font-semibold transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1C3A7A] transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="hover:text-[#1C3A7A] transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 pt-28 pb-20">
        
        {/* Impact Statistics */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          <div className="bg-white border border-gray-100 shadow-lg rounded-2xl p-4 text-center hover:shadow-xl transition-shadow">
            <div className="text-3xl md:text-4xl font-bold text-[#FAB710] mb-1">23M+</div>
            <div className="text-gray-600 text-xs md:text-sm">Americans in Recovery</div>
          </div>
          <div className="bg-white border border-gray-100 shadow-lg rounded-2xl p-4 text-center hover:shadow-xl transition-shadow">
            <div className="text-3xl md:text-4xl font-bold text-[#FAB710] mb-1">17</div>
            <div className="text-gray-600 text-xs md:text-sm">Years in the Making</div>
          </div>
          <div className="bg-white border border-gray-100 shadow-lg rounded-2xl p-4 text-center hover:shadow-xl transition-shadow">
            <div className="text-3xl md:text-4xl font-bold text-[#FAB710] mb-1">1</div>
            <div className="text-gray-600 text-xs md:text-sm">Mission: Connection</div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          {/* Photo */}
          <div className="relative inline-block mb-6">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-full bg-gradient-to-br from-[#FAB710] to-[#f59e0b] p-1.5 shadow-2xl shadow-[#FAB710]/30">
              <img 
                src="/images/founder-headshot.jpg"
                alt="Tracy Perrin"
                className="w-full h-full rounded-full object-cover"
              />
            </div>
            {/* 35 Years Badge */}
            <div className="absolute -bottom-2 -right-2 bg-white rounded-full px-4 py-2 shadow-lg border border-gray-100">
              <span className="text-[#1C3A7A] font-bold text-sm">35 Years Clean</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-[#1C3A7A] mb-2">
            Tracy Perrin
          </h1>
          <p className="text-[#FAB710] text-xl font-medium">
            Founder of RecoveryMatch
          </p>
        </div>

        {/* Vision Since 2009 */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-[#FAB710]/30 rounded-2xl p-6 mb-10 text-center">
          <Calendar className="w-8 h-8 text-[#FAB710] mx-auto mb-3" />
          <p className="text-gray-700 text-lg">
            <span className="font-bold text-[#FAB710]">RecoveryMatch.com</span> — a domain held since 
            <span className="font-bold text-[#FAB710]"> 2009</span>, waiting for the right moment to become something meaningful.
          </p>
          <p className="text-gray-500 text-sm mt-2">That moment is now.</p>
        </div>

        {/* Quote Section */}
        <div className="bg-gradient-to-br from-[#1C3A7A] to-[#0f2550] rounded-2xl p-6 md:p-8 mb-10 relative shadow-xl">
          <Quote className="w-10 h-10 text-[#FAB710]/50 absolute top-4 left-4" />
          <blockquote className="text-white text-xl md:text-2xl font-light italic text-center px-8 py-4">
            "Recovery gave me my life back. RecoveryMatch is my way of helping others 
            find the connection and support that made all the difference in my journey."
          </blockquote>
        </div>

        {/* Bio Section */}
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl border border-gray-100">
          <h2 className="text-2xl font-bold text-[#1C3A7A] mb-6 flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#FAB710]" />
            My Story
          </h2>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-5">
            <p>
              Thirty-five years ago, I made a decision that would change everything. 
              Like so many others, I had reached a crossroads where the path I was on 
              could only lead to destruction. Choosing recovery wasn't just about 
              putting down substances—it was about rebuilding my entire life from the ground up.
            </p>
            
            <p>
              In those early days and through the years that followed, I learned that 
              recovery isn't something we do alone. The relationships I formed in the 
              rooms—with sponsors and other recovering individuals—became the 
              foundation of my new life. These connections understood me in ways others 
              couldn't. They knew the struggle, celebrated the victories, and showed up 
              during the hard days without judgment.
            </p>
            
            <p>
              But I also watched too many people in recovery struggle to find meaningful 
              romantic connections. Traditional dating apps felt shallow and triggering. 
              Meeting people in bars was obviously not an option. And explaining our 
              recovery journey to someone who doesn't understand it can feel impossible.
            </p>

            <p className="text-[#1C3A7A] font-medium text-xl">
              That's why I created RecoveryMatch.
            </p>
            
            <p>
              This isn't just another dating app. RecoveryMatch was built from the ground 
              up by someone who has lived this journey. Every feature was designed with 
              intention: the thoughtful questionnaire that helps find truly compatible 
              matches, the guided conversation starters that ease the awkwardness of 
              first messages, and the deliberate pace that mirrors the "easy does it" 
              philosophy we know so well.
            </p>
            
            <p>
              We don't do endless swiping here—because recovery taught us that the 
              quick fix rarely leads to lasting happiness. Instead, we focus on quality 
              connections, shared values, and the understanding that only comes from 
              walking a similar path.
            </p>
          </div>

          {/* Mission Section */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FAB710]" />
              Our Mission
            </h3>
            <p className="text-gray-700 text-lg">
              To create a safe, understanding space where people in recovery can find 
              meaningful connections—romantic partners who get it, who support the journey, 
              and who are building their own beautiful lives one day at a time.
            </p>
            
            {/* Signature */}
            <div className="mt-8 text-left">
              <p className="text-gray-600 italic mb-2">Sincerely,</p>
              <p className="text-[#1C3A7A] font-semibold text-lg">Tracy Perrin</p>
              <p className="text-gray-500 text-sm">Founder, RecoveryMatch.com</p>
              <div className="mt-3">
                <span 
                  className="text-4xl text-[#1C3A7A]" 
                  style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive" }}
                >
                  Tracy Perrin
                </span>
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-xl p-5 text-center border border-blue-100">
              <div className="w-12 h-12 bg-[#1C3A7A] rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-[#1C3A7A] mb-1">Intentional</h4>
              <p className="text-sm text-gray-600">Quality over quantity, always</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 text-center border border-amber-100">
              <div className="w-12 h-12 bg-[#FAB710] rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-[#1C3A7A] mb-1">Understanding</h4>
              <p className="text-sm text-gray-600">Built by and for people in recovery</p>
            </div>
            <div className="bg-green-50 rounded-xl p-5 text-center border border-green-100">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-bold text-[#1C3A7A] mb-1">Hopeful</h4>
              <p className="text-sm text-gray-600">Because we know miracles happen</p>
            </div>
          </div>

          {/* Closing */}
          <div className="mt-10 text-center">
            <p className="text-gray-600 italic mb-6">
              "One day at a time, one connection at a time."
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/register")}
                className="bg-[#1C3A7A] hover:bg-[#152d61] text-white px-8 py-3 rounded-full text-lg"
              >
                Join Our Community
              </Button>
              <Button
                onClick={() => navigate("/guide")}
                className="inline-flex items-center justify-center gap-2 bg-[#FAB710] hover:bg-[#e5a60f] text-[#1C3A7A] px-8 py-3 rounded-full text-lg font-semibold transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Read Our Guide
              </Button>
            </div>
          </div>
        </div>

        {/* Why This Matters - Recovery Statistics Section */}
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl mt-10 border border-gray-100">
          <h2 className="text-2xl font-bold text-[#1C3A7A] mb-6 flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-[#FAB710]" />
            Why This Matters
          </h2>
          
          <p className="text-gray-700 text-lg mb-8">
            Finding love in recovery comes with unique challenges. Here's the reality that inspired RecoveryMatch:
          </p>

          {/* Statistics Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Stat 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <div className="text-4xl font-bold text-[#1C3A7A] mb-2">40-60%</div>
              <div className="text-gray-700 font-medium mb-2">Relapse Rate in First Year</div>
              <p className="text-gray-600 text-sm">
                Relationships with partners who don't understand recovery can be a major trigger. Having a partner who "gets it" significantly reduces this risk.
              </p>
            </div>

            {/* Stat 2 */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-xl p-6 border border-amber-200">
              <div className="text-4xl font-bold text-[#FAB710] mb-2">75%</div>
              <div className="text-gray-700 font-medium mb-2">Feel Misunderstood on Dating Apps</div>
              <p className="text-gray-600 text-sm">
                Most people in recovery report feeling the need to hide their journey on mainstream dating platforms, leading to inauthentic connections.
              </p>
            </div>

            {/* Stat 3 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
              <div className="text-4xl font-bold text-green-600 mb-2">50%+</div>
              <div className="text-gray-700 font-medium mb-2">Higher Success with Shared Experience</div>
              <p className="text-gray-600 text-sm">
                Couples where both partners are in recovery report higher relationship satisfaction and mutual understanding of boundaries.
              </p>
            </div>

            {/* Stat 4 */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
              <div className="text-4xl font-bold text-purple-600 mb-2">80%</div>
              <div className="text-gray-700 font-medium mb-2">Say Social Support is Critical</div>
              <p className="text-gray-600 text-sm">
                Research consistently shows that strong social connections—including romantic relationships—are one of the biggest predictors of long-term sobriety.
              </p>
            </div>
          </div>

          {/* The Challenge */}
          <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 mb-8">
            <h3 className="text-lg font-bold text-red-700 mb-2">The Dating Dilemma</h3>
            <ul className="text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-1">•</span>
                <span><strong>Bars and clubs</strong> — Not an option for people protecting their sobriety</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-1">•</span>
                <span><strong>Traditional dating apps</strong> — Often surface-level, triggering, or require explaining your journey to someone who may not understand</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-1">•</span>
                <span><strong>Meeting in the rooms</strong> — Complicated by the "no 13th stepping" guidance many programs recommend</span>
              </li>
            </ul>
          </div>

          {/* The Solution */}
          <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4a8a] rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#FAB710]" />
              RecoveryMatch: The Solution
            </h3>
            <p className="text-white/90 mb-4">
              We created a dedicated space where people in recovery can be their authentic selves from the start. No explaining, no judgment, no risk—just genuine connections with people who understand the journey.
            </p>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-[#FAB710]">100%</div>
                <div className="text-white/70 text-sm">Recovery-Focused</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-[#FAB710]">Safe</div>
                <div className="text-white/70 text-sm">& Supportive</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-[#FAB710]">Real</div>
                <div className="text-white/70 text-sm">Connections</div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="text-center mt-10 mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <a 
              href="https://www.facebook.com/RecoveryMatch/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#FAB710] transition"
              title="Follow us on Facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href="https://www.instagram.com/RecoveryMatch/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#FAB710] transition"
              title="Follow us on Instagram"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>
          <p className="text-gray-500 text-sm">
            RecoveryMatch — Where recovery meets connection
          </p>
        </div>
      </div>

      {/* Standard Footer */}
      <Footer />
    </div>
  );
};

export default FounderPage;
